import {
  Input,
  inject,
  OnInit,
  signal,
  effect,
  computed,
  Component,
  OnChanges,
  DestroyRef,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormControl } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { DialogCloseReason, DialogService } from '@sui/dialog';

import { LoggerService } from '@services/logger.service';
import { FormsService } from '../../services/forms.service';
import { Permission, Principal } from '../../models/object.models';
import { UserAccessLevel } from '../../services/user.service';

import { GeneralDynamicFormComponent } from '../general-dynamic-form/general-dynamic-form.component';
import { PickerDataobjectsDialogComponent } from '../popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

import {
  PopupType,
  NomObjectClass,
  NomObjectPermissionRole,
} from '../../constants';

import { DataObject, ObjectDefinition } from '../../models/core.models';
import { clearSelectedPrincipals } from '../../actions/principals.actions';
import {
  injectDefaultPermissionsQuery,
  injectObjectsPermissionsQuery,
} from 'src/app/api/queries/permissions.queries';
import { getPermissionIdsFromRole } from '../../permission.utils';

import { selectUser } from '../../reducers';
import { AppState } from '../../../reducers';

import { take, switchMap, tap, filter, map } from 'rxjs';

import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
// object-permissions-wrapper.component.ts (add/adjust)
type BuilderType = 'simple' | 'advanced';
@Component({
  selector: 'app-object-permissions',
  templateUrl: './object-permissions.component.html',
  styleUrl: './object-permissions.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ObjectPermissionsComponent
  extends GeneralDynamicFormComponent
  implements OnInit, OnChanges
{
  @Input() objects: DataObject[] = [];
  @Input() startInAdvanced = false;
  @Input() companyId: string = ''; // needed for principal loading

  @Input() objectClassId: number | null = null; // optional, for default permissions

  // ownership: hidden unless the host dialog opts in; gated to client admins and above
  @Input() allowOwnerChange = false;
  @Input() ownerChangeMinAccessLevel: UserAccessLevel =
    UserAccessLevel.CLIENT_ADMIN;

  // * the edit model lives here, in signals - NOT in the query cache. The matrix
  // * adds/removes principals and edits roles locally before the save posts the
  // * result, so a refetch must never see or revert a pending edit. Both signals are
  // * reseeded whenever a fetch delivers.
  readonly principals = signal<Principal[]>([]);
  readonly permissions = signal<Permission[]>([]);

  // * the inputs decide which of the two reads is active
  private readonly permissionsObjects = signal<DataObject[] | null>(null);
  private readonly defaultPermissionsClassId = signal<number | null>(null);

  private readonly objectsPermissionsQuery = injectObjectsPermissionsQuery(
    this.permissionsObjects,
  );
  private readonly defaultPermissionsQuery = injectDefaultPermissionsQuery(
    this.defaultPermissionsClassId,
  );

  readonly loading = computed(
    () =>
      this.objectsPermissionsQuery.isLoading() ||
      this.defaultPermissionsQuery.isLoading(),
  );

  // ownership state — persisted through the host dialog's main save as owner[...] params
  canChangeOwner = false;
  pendingOwner: Principal | null = null;
  readonly ownerControls = {
    newOwnerId: new FormControl<string | null>(null),
    applyToChildObjects: new FormControl(false),
    applyToSameOwnerChildObjectsOnly: new FormControl(false),
  };
  private readonly ownerControlKeys: Record<
    keyof ObjectPermissionsComponent['ownerControls'],
    string
  > = {
    newOwnerId: 'owner[new_owner_id]',
    applyToChildObjects: 'owner[apply_to_child_objects]',
    applyToSameOwnerChildObjectsOnly:
      'owner[apply_to_same_owner_child_objects_only]',
  };

  // UI
  isAdvancedPermissions = false;

  // constants for children
  NomObjectPermission = NomObjectPermissionRole;
  permissionRoleList: Array<{ id: number; value: string }> = [];

  protected destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);

  visibleTabs = [
    {
      key: 'simple' as BuilderType,
      label: 'object-permissions.simple',
      icon: ['far', 'square-check'] as IconProp,
    },
    {
      key: 'advanced' as BuilderType,
      label: 'object-permissions.advanced',
      icon: ['fas', 'sliders-h'] as IconProp,
    },
  ];

  builderType: BuilderType = this.startInAdvanced ? 'advanced' : 'simple';

  constructor(
    protected store: Store<AppState>,
    protected translate: TranslateService,
    protected formService: FormsService,
    protected logger: LoggerService,
    private utilsDataObjectApi: UtilsDataObjectApiService,
  ) {
    super(formService, store, logger);

    // * Reseed the edit model from whichever read is active. Structural sharing keeps
    // * the same reference when a refetch returns identical data, so this does not
    // * clobber edits on a no-op refetch.
    effect(() => {
      const data =
        this.objectsPermissionsQuery.data() ??
        this.defaultPermissionsQuery.data();

      if (!data) {
        return;
      }

      this.principals.set(data.principals);
      this.permissions.set(data.permissions);
    });
  }

  toggleAdvancedPermissions(next: boolean) {
    // keep backward compatibility with your existing calls
    this.isAdvancedPermissions = next;
    this.builderType = next ? 'advanced' : 'simple';
  }

  changeBuilderType(type: BuilderType) {
    if (this.builderType === type) return;
    this.builderType = type;
    this.isAdvancedPermissions = type === 'advanced';
  }

  ngOnInit(): void {
    super.ngOnInit();

    // console.log(
    //   'ObjectPermissions formGroup:',
    //   this.formGroup,
    //   'valid:',
    //   this.formGroup.valid,
    // );

    // track when formGroup status changes
    // TODO: remove once we done with debugging!
    this.formGroup.statusChanges
      .pipe(
        tap((status) => {
          // console.log(
          //   '%c ObjectPermissions formGroup status changed -->> ',
          //   'color: orange; font-weight: bold;',
          //   status,
          //   'valid:',
          //   this.formGroup.valid,
          //   'controls:',
          //   Object.keys(this.formGroup.controls),
          //   this.formGroup,
          // );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.isAdvancedPermissions = this.startInAdvanced;
    this.builderType = this.isAdvancedPermissions ? 'advanced' : 'simple';
    this.buildPermissionRoleList();
    this.loadPermissionsForObjects();

    this.store
      .pipe(
        select(selectUser),
        tap((user) => {
          this.canChangeOwner =
            this.allowOwnerChange &&
            !!user &&
            user.access_level_id >= this.ownerChangeMinAccessLevel;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store.dispatch(
      clearSelectedPrincipals({
        companyId: this.companyId,
      }),
    );
  }

  get currentOwner(): Principal | undefined {
    return this.objects?.[0]?.owner;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['objects'] && !changes['objects'].firstChange) {
      this.loadPermissionsForObjects();
    }
  }

  private buildPermissionRoleList() {
    this.permissionRoleList = [
      {
        id: NomObjectPermissionRole.OWNER,
        value: this.translate.instant('object-permissions.role-list.owner'),
      },
      {
        id: NomObjectPermissionRole.VIEWER,
        value: this.translate.instant('object-permissions.role-list.viewer'),
      },
      {
        id: NomObjectPermissionRole.EDITOR,
        value: this.translate.instant('object-permissions.role-list.editor'),
      },
      {
        id: NomObjectPermissionRole.CUSTOM,
        value: this.translate.instant('object-permissions.role-list.custom'),
      },
      {
        id: NomObjectPermissionRole.ADMIN,
        value: this.translate.instant('object-permissions.role-list.admin'),
      },
    ];
  }

  private loadPermissionsForObjects() {
    // * a scope change re-keys the query, which is what fetches
    if (this.objects?.length) {
      this.permissionsObjects.set(this.objects);
      this.defaultPermissionsClassId.set(null);
    } else if (this.objectClassId !== null) {
      this.permissionsObjects.set(null);
      this.defaultPermissionsClassId.set(this.objectClassId);
    }
  }

  onRemovePrincipal(principal: Principal) {
    // children already block removing owners if needed
    this.principals.update((principals) =>
      principals.filter((p) => p.id !== principal.id),
    );
    this.formGroup.markAsDirty();
  }

  /**
   * Add a principal to the matrix: skip duplicates, default to VIEWER, and grant
   * that role's permissions so the advanced matrix shows the new row already ticked.
   */
  private addPrincipalToModel(principal: Principal) {
    if (this.principals().some((p) => p.id === principal.id)) {
      return;
    }

    const newPrincipal: Principal = {
      ...principal,
      permission_role_id: NomObjectPermissionRole.VIEWER,
    };
    const rolePermissions = getPermissionIdsFromRole(
      newPrincipal.permission_role_id,
    );

    this.principals.update((principals) => [...principals, newPrincipal]);
    this.permissions.update((permissions) =>
      permissions.map((permission) =>
        permission.permission_id &&
        rolePermissions.includes(permission.permission_id)
          ? {
              ...permission,
              principals: [
                ...permission.principals,
                {
                  principal_id: principal.id,
                  principal_class_id: principal.object_class_id,
                },
              ],
            }
          : permission,
      ),
    );
  }

  onAddPrincipalsRequest() {
    // open the picker, then add
    this.store.dispatch(
      clearSelectedPrincipals({
        companyId: this.companyId,
      }),
    );
    const dialogRef = this.dialogService.openSimpleDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds: [NomObjectClass.USER, NomObjectClass.USER_GROUP],
          popupType: PopupType.ADD_PRINCIPALS,
          useObjectMap: true,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event?.data as ObjectDefinition[]),
        filter((objectDefinitions) => !!objectDefinitions?.length),
        switchMap((objectDefinitions: ObjectDefinition[]) =>
          this.utilsDataObjectApi
            .getPrincipalsByIds(objectDefinitions)
            .pipe(take(1)),
        ),
        tap((response: any) => {
          response?.items.forEach((principal) =>
            this.addPrincipalToModel(principal),
          );
          if (response.items.length > 0) {
            this.formGroup.markAsDirty();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSelectNewOwner(): void {
    this.store.dispatch(
      clearSelectedPrincipals({
        companyId: this.companyId,
      }),
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds: [NomObjectClass.USER],
          singleSelectionActive: true,
          useObjectMap: true,
          popupType: PopupType.SELECT_NEW_OWNER,
          // Only the current owner is excluded; a principal that already holds
          // a permission is a valid new owner (WAP-1660).
          lockedUserIds: this.currentOwner ? [this.currentOwner.id] : [],
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event?.data as ObjectDefinition[]),
        filter((objectDefinitions) => !!objectDefinitions?.length),
        switchMap((objectDefinitions: ObjectDefinition[]) =>
          this.utilsDataObjectApi
            .getPrincipalsByIds(objectDefinitions)
            .pipe(take(1)),
        ),
        tap((response: any) => {
          const principal: Principal | undefined = response?.items?.[0];
          if (principal) {
            this.setPendingOwner(principal);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  clearPendingOwner(): void {
    this.pendingOwner = null;
    this.ownerControls.newOwnerId.setValue(null);
    this.ownerControls.applyToChildObjects.setValue(false);
    this.ownerControls.applyToSameOwnerChildObjectsOnly.setValue(false);
    Object.values(this.ownerControlKeys).forEach((key) =>
      this.formGroup.removeControl(key),
    );
  }

  private setPendingOwner(principal: Principal): void {
    this.pendingOwner = principal;
    this.registerOwnerControls();
    this.ownerControls.newOwnerId.setValue(principal.id);
    this.formGroup.markAsDirty();
  }

  // owner[...] controls only exist in the form while a new owner is pending,
  // so a save without an ownership change sends no owner params at all
  private registerOwnerControls(): void {
    Object.entries(this.ownerControlKeys).forEach(([name, key]) => {
      if (!this.formGroup.contains(key)) {
        this.formGroup.addControl(key, this.ownerControls[name]);
      }
    });
  }

  // when a role is changed in the simple component's form
  onPrincipalRoleChange(evt: { controlKey: string; roleId: number }) {
    // You likely already read the final form from the parent to submit.
    // This hook is here in case you want to optimistically update something in NgRx.
    // For now, we leave it as a placeholder.
    this.formGroup.markAsDirty();
  }

  // advanced: when a single permission checkbox is toggled
  // * nothing to record here: the checkbox state lives in the advanced child's form
  // * controls, which is what the save reads
  onPermissionToggle(evt: {
    permission: any;
    principal: any;
    checked: boolean;
  }) {
    this.formGroup.markAsDirty();
  }

  // advanced: when "toggle all" is used for a principal
  onToggleAllForPrincipal(evt: { principal: any; checked: boolean }) {
    // Optional: same as above — if you want instant persistence, dispatch here.
    this.formGroup.markAsDirty();
  }
}
